// src/pages/AuthPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loginUser, registerUser, loginWithGoogle, resetPassword, checkEmailExists } from '../firebase/services';
import Toast from '../components/Toast';

function AuthPage({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [tab,          setTab]          = useState(mode);
  const [showPw,       setShowPw]       = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [resetMode,    setResetMode]    = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [toast,        setToast]        = useState(null);
  const [form,         setForm]         = useState({ name: '', email: '', password: '', confirm: '', phone: '' });

  // emailStatus: 'idle' | 'checking' | 'exists' | 'free' | 'unknown'
  const [emailStatus,  setEmailStatus]  = useState('idle');


  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reset status when switching tabs
  useEffect(() => {
    setEmailStatus('idle');
  }, [tab]);

  const handleEmailChange = (v) => {
    s('email', v);
    setEmailStatus('idle');
  };

  const handleEmailBlur = async () => {
    if (tab !== 'register' || resetMode) return;
    const email = form.email.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailStatus('idle');
      return;
    }
    setEmailStatus('checking');
    const result = await checkEmailExists(email);
    if (result.exists === true) {
      setEmailStatus('exists');
    } else if (result.exists === false) {
      setEmailStatus('free');
      } else {
      // null = unknown (rate limited etc) — don't block, just stay quiet
      setEmailStatus('idle');
      }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Block submission if email is taken
    if (tab === 'register' && emailStatus === 'exists') return;
    setLoading(true);
    try {
      if (resetMode) {
        await resetPassword(form.email);
        setToast({ message: 'Reset email sent! Check your inbox.', type: 'success' });
        setResetMode(false);
      } else if (tab === 'register') {
        if (form.password !== form.confirm) throw new Error('Passwords do not match');
        if (form.password.length < 6) throw new Error('Password must be at least 6 characters');
        await registerUser(form.email, form.password, form.name, form.phone.trim());
        navigate(from);
      } else {
        await loginUser(form.email, form.password);
        navigate(from);
      }
    } catch (err) {
      const code = err.code || '';
      const friendlyErrors = {
        'auth/email-already-in-use':   'This email is already registered. Try signing in.',
        'auth/user-not-found':         'No account found with this email.',
        'auth/wrong-password':         'Incorrect password. Try again or reset it.',
        'auth/invalid-email':          'Please enter a valid email address.',
        'auth/weak-password':          'Password must be at least 6 characters.',
        'auth/too-many-requests':      'Too many attempts. Please wait a moment.',
        'auth/network-request-failed': 'Network error. Check your connection.',
        'auth/popup-closed-by-user':   'Google sign-in was cancelled.',
        'auth/invalid-credential':     'Incorrect email or password. Please try again.',
      };
      const msg = friendlyErrors[code] || err.message || 'Authentication failed';
      setToast({ message: msg, type: 'error' });
      if (code === 'auth/email-already-in-use') {
        setEmailStatus('exists');
        setTimeout(() => setTab('login'), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      await loginWithGoogle();
      navigate(from);
    } catch {
      setToast({ message: 'Google sign-in failed', type: 'error' });
    }
  };

  const labelStyle = {
    display: 'block', fontSize: 10, fontWeight: 600,
    letterSpacing: '0.15em', textTransform: 'uppercase',
    color: 'var(--gold)', marginBottom: 8,
  };

  const isChecking = emailStatus === 'checking';
  const isExists   = emailStatus === 'exists';
  const isFree     = emailStatus === 'free';

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'minmax(0,480px) 1fr' }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ── Form Panel ── */}
      <div style={{
        background: 'var(--bg-2)', borderRight: '1px solid var(--border)',
        padding: 'clamp(32px,6vw,56px) clamp(20px,5vw,52px)', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', overflow: 'auto',
      }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 52, textDecoration: 'none' }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--gold), var(--gold-3))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Playfair Display, serif', fontSize: 14, fontWeight: 700, color: '#09080A',
          }}>S</div>
          <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, letterSpacing: '0.1em' }}>ShopWorld</span>
        </Link>

        {/* Tab switcher */}
        {!resetMode && (
          <div style={{ display: 'flex', marginBottom: 36, border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            {['login', 'register'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: '11px', fontSize: 11, fontWeight: 600,
                letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer',
                background: tab === t ? 'var(--gold)' : 'transparent',
                color: tab === t ? '#09080A' : 'var(--text-3)',
                transition: 'all 0.25s',
              }}>
                {t === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>
        )}

        <div className="divider" style={{ marginLeft: 0, marginBottom: 28 }} />

        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400, marginBottom: 6 }}>
          {resetMode ? 'Reset Password' : tab === 'login' ? 'Welcome Back' : 'Create Account'}
        </h2>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 32 }}>
          {resetMode
            ? "We'll send a reset link to your email."
            : tab === 'login'
            ? 'Access your ShopWorld account.'
            : 'Join the world of curated luxury.'}
        </p>

        <form onSubmit={handleSubmit}>

          {/* Full Name (register only) */}
          {tab === 'register' && !resetMode && (
            <div className="form-group">
              <label style={labelStyle}>Full Name</label>
              <input className="input" value={form.name}
                onChange={e => s('name', e.target.value)}
                placeholder="Your full name" required />
            </div>
          )}

          {/* Mobile Number (register only) */}
          {tab === 'register' && !resetMode && (
            <div className="form-group">
              <label style={labelStyle}>Mobile Number</label>
              <input
                className="input"
                type="tel"
                value={form.phone}
                onChange={e => s('phone', e.target.value)}
                placeholder="10-digit mobile number"
                pattern="[0-9]{10}"
                maxLength={10}
                required
              />
            </div>
          )}

          {/* Email */}
          <div className="form-group">
            <label style={labelStyle}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={e => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="you@example.com"
                required
                style={{
                  paddingRight: 36,
                  ...(isExists
                    ? { borderColor: '#E53E3E', boxShadow: '0 0 0 3px rgba(229,62,62,0.12)' }
                    : isFree
                    ? { borderColor: '#38A169', boxShadow: '0 0 0 3px rgba(56,161,105,0.12)' }
                    : {}),
                }}
              />
              {/* Inline status icon */}
              {tab === 'register' && !resetMode && (
                <span style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 14, pointerEvents: 'none',
                  display: isChecking || isExists || isFree ? 'block' : 'none',
                }}>
                  {isChecking ? '⏳' : isExists ? '✗' : isFree ? '✓' : ''}
                </span>
              )}
            </div>

            {/* Status messages — only shown during register */}
            {tab === 'register' && !resetMode && (
              <>
                {/* Checking spinner */}
                {isChecking && (
                  <div style={{ marginTop: 7, fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{
                      display: 'inline-block', width: 11, height: 11,
                      border: '2px solid #ddd', borderTopColor: '#C9A84C',
                      borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0,
                    }} />
                    Checking availability...
                  </div>
                )}

                {/* Email already exists */}
                {isExists && !isChecking && (
                  <div style={{
                    marginTop: 10, padding: '12px 16px',
                    background: 'rgba(229,62,62,0.07)',
                    border: '1px solid rgba(229,62,62,0.25)',
                    borderRadius: 8, fontSize: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}>
                    <span style={{ color: '#C53030' }}>
                      ⚠️ This email is already registered.
                    </span>
                    <button type="button" onClick={() => setTab('login')} style={{
                      background: 'none', border: '1px solid rgba(229,62,62,0.35)',
                      color: '#C53030', cursor: 'pointer', fontWeight: 700,
                      padding: '4px 12px', borderRadius: 5, fontSize: 11,
                      whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'Inter, sans-serif',
                    }}>
                      Sign in →
                    </button>
                  </div>
                )}

                {/* Email is available */}
                {isFree && !isChecking && (
                  <div style={{ marginTop: 7, fontSize: 12, color: '#2F855A', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>✓</span> Email is available — you're good to go!
                  </div>
                )}
              </>
            )}
          </div>

          {/* Password */}
          {!resetMode && (
            <div className="form-group">
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => s('password', e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  style={{ paddingRight: 48 }}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16,
                }}>
                  {showPw ? '🙈' : '👁'}
                </button>
              </div>
            </div>
          )}

          {/* Confirm Password (register only) */}
          {tab === 'register' && !resetMode && (
            <div className="form-group">
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={e => s('confirm', e.target.value)}
                  placeholder="Repeat password"
                  required
                  style={{
                    paddingRight: 48,
                    ...(form.confirm && form.password !== form.confirm
                      ? { borderColor: '#E53E3E' }
                      : form.confirm && form.password === form.confirm
                      ? { borderColor: '#38A169' }
                      : {}),
                  }}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16,
                }}>
                  {showConfirm ? '🙈' : '👁'}
                </button>
              </div>
              {form.confirm && form.password !== form.confirm && (
                <div style={{ marginTop: 5, fontSize: 12, color: '#C53030' }}>✗ Passwords do not match</div>
              )}
              {form.confirm && form.password === form.confirm && (
                <div style={{ marginTop: 5, fontSize: 12, color: '#2F855A' }}>✓ Passwords match</div>
              )}
            </div>
          )}

          {/* Forgot password */}
          {tab === 'login' && !resetMode && (
            <div style={{ textAlign: 'right', marginBottom: 24, marginTop: -8 }}>
              <button type="button" onClick={() => setResetMode(true)} style={{
                background: 'none', border: 'none', fontSize: 11, color: 'var(--text-3)',
                cursor: 'pointer', letterSpacing: '0.05em', transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.target.style.color = 'var(--gold)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-3)'}
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            className="btn btn-gold btn-full btn-lg"
            disabled={loading || isExists || isChecking}
            style={{ marginBottom: resetMode ? 12 : 0, opacity: (isExists || isChecking) ? 0.5 : 1 }}
          >
            {loading
              ? 'Please wait...'
              : resetMode
              ? 'Send Reset Link'
              : tab === 'login'
              ? 'Sign In →'
              : 'Create Account →'}
          </button>

          {resetMode && (
            <button type="button" onClick={() => setResetMode(false)}
              className="btn btn-ghost btn-full" style={{ marginTop: 4 }}>
              ← Back to Sign In
            </button>
          )}
        </form>

        {/* Google sign-in */}
        {!resetMode && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '24px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.08em' }}>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <button onClick={handleGoogle} className="btn btn-outline btn-full" style={{ gap: 10, marginBottom: 28 }}>
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </>
        )}
      </div>

      {/* ── Right Image Panel ── */}
      <div style={{
        background: `url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200') center/cover`,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(9,12,10,0.6) 0%, rgba(9,12,10,0.2) 100%)' }} />
        <div style={{ position: 'absolute', bottom: 56, left: 56, right: 56 }}>
          <div className="divider" style={{ marginLeft: 0, width: 50, marginBottom: 28 }} />
          <blockquote style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(1.3rem, 2.5vw, 1.9rem)',
            fontStyle: 'italic', fontWeight: 400, lineHeight: 1.45,
            color: 'rgba(255,255,255,0.92)', marginBottom: 16,
          }}>
            "The finest food and the most exquisite things in life, together at last."
          </blockquote>
          <cite style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            — ShopWorld Experience
          </cite>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          div[style*="minmax(0,480px)"] {
            grid-template-columns: 1fr !important;
          }
          div[style*="minmax(0,480px)"] > div:last-of-type {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          div[style*="minmax(0,480px)"] {
            min-height: 100vh;
          }
        }
      `}</style>
    </div>
  );
}

export const LoginPage    = () => <AuthPage mode="login" />;
export const RegisterPage = () => <AuthPage mode="register" />;
